import React from "react";
import { Link } from "react-router-dom";

import {
  Email,
  IncidentIoLogo,
  PagerDutyLogo,
  SlackLogo,
  WebhookIcon,
} from "ui/Icons";
import { useRoutes } from "utils/routes";

type Mechanism = "slack" | "pagerduty" | "email" | "webhook" | "incident_io";

const MECHANISM_ICON: Record<Mechanism, React.ComponentType> = {
  slack: SlackLogo,
  pagerduty: PagerDutyLogo,
  email: Email,
  webhook: WebhookIcon,
  incident_io: IncidentIoLogo,
};

// Renders a mechanism's icon in a fixed-width slot so the wider e-mail glyph
// lines up with the 16px Slack/PagerDuty logos.
function MechanismIcon({ mechanism }: { mechanism: string }) {
  const Icon = MECHANISM_ICON[mechanism as Mechanism];
  if (!Icon) {
    return null;
  }
  return (
    <span className="inline-flex w-4 shrink-0 items-center justify-center">
      <Icon />
    </span>
  );
}

// A single delivery target (one Slack channel / PagerDuty service, or an email
// recipient summary), shown as a small pill with its mechanism icon. Spacing is
// left to the parent (e.g. a `gap`-based flex row) so it fits both wrapping lists
// and single-line clipped rows.
export function TargetTag({
  mechanism,
  label,
}: {
  mechanism: string;
  label: string;
}) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded border border-[#ddd] bg-white px-[6px] py-[2px] align-middle text-[12px]">
      <MechanismIcon mechanism={mechanism} />
      <span className="text-sm">{label}</span>
    </span>
  );
}

// A destination's name rendered as a link, for the several places that list
// destinations as middle-dot-separated names (rather than pills). Sorted
// alphabetically; callers dedupe first if a destination can appear more than once.
export function DestinationLinks({
  destinations,
  organizationSlug,
}: {
  destinations: { id: string; name: string }[];
  organizationSlug: string;
}) {
  const { organizationAlertDestination } = useRoutes();
  return (
    <>
      {[...destinations]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((d, i) => (
          <React.Fragment key={d.id}>
            {i > 0 && " · "}
            <Link to={organizationAlertDestination(organizationSlug, d.id)}>
              {d.name}
            </Link>
          </React.Fragment>
        ))}
    </>
  );
}
